import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { adminService } from '../../services/admin.service';
import { FaEdit, FaTrash, FaPlus, FaGlobe } from 'react-icons/fa';
import AdminNavbar from '../../components/admin/AdminNavbar';
import Swal from 'sweetalert2';

const DomainsList = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      const data = await adminService.getDomains();
      setDomains(data);
    } catch (error) {
      toast.error('Error al cargar los dominios');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (domain) => {
    const result = await Swal.fire({
      title: '¿Eliminar dominio?',
      html: `Se eliminará la configuración de <strong>${domain.hostname}</strong>. Las campañas asociadas no se verán afectadas.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await adminService.deleteDomain(domain._id);
        toast.success('Dominio eliminado correctamente');
        loadDomains();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error al eliminar el dominio');
      }
    }
  };

  if (loading) {
    return <LoadingMessage>Cargando dominios...</LoadingMessage>;
  }

  return (
    <>
      <AdminNavbar />
      <Container>
        <Header>
          <Title>Dominios</Title>
          <AddButton onClick={() => navigate('/pmpl/admin/domains/new')}>
            <FaPlus /> Nuevo Dominio
          </AddButton>
        </Header>

        <DomainsGrid>
          {domains.map(domain => (
            <DomainCard key={domain._id}>
              <DomainHeader>
                <DomainHostname>
                  <FaGlobe /> {domain.hostname}
                </DomainHostname>
                <StatusBadge active={domain.isActive}>
                  {domain.isActive ? 'Activo' : 'Inactivo'}
                </StatusBadge>
              </DomainHeader>

              <DomainInfo>
                <InfoItem>
                  <Label>Nombre:</Label>
                  <Value>{domain.displayName}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>App:</Label>
                  <Value>{domain.appName || `Rasca y Gana de ${domain.displayName}`}</Value>
                </InfoItem>
                <InfoItem>
                  <Label>Corto:</Label>
                  <Value>{domain.shortName}</Value>
                </InfoItem>
              </DomainInfo>

              <Actions>
                <ActionButton onClick={() => navigate(`/pmpl/admin/domains/${domain._id}/edit`)}>
                  <FaEdit /> Editar
                </ActionButton>
                <ActionButton danger onClick={() => handleDelete(domain)}>
                  <FaTrash /> Eliminar
                </ActionButton>
              </Actions>
            </DomainCard>
          ))}
        </DomainsGrid>

        {domains.length === 0 && (
          <EmptyState>
            <p>No hay dominios configurados.</p>
            <p>Crea uno para personalizar el nombre de la app en cada dominio.</p>
            <AddButton onClick={() => navigate('/pmpl/admin/domains/new')}>
              <FaPlus /> Crear primer dominio
            </AddButton>
          </EmptyState>
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #ffd700;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #ffd700;
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #ffed4a;
  }
`;

const DomainsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const DomainCard = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: 2fr 1fr auto;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DomainHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DomainHostname = styled.h3`
  color: #fff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  background: ${props => props.active ? '#34D399' : '#6B7280'};
  color: white;
  width: fit-content;
`;

const DomainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.span`
  color: #999;
  font-size: 0.8rem;
`;

const Value = styled.span`
  color: #fff;
  font-size: 0.95rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  background: ${props => props.danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 215, 0, 0.1)'};
  color: ${props => props.danger ? '#EF4444' : '#ffd700'};

  &:hover {
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 215, 0, 0.2)'};
  }

  @media (max-width: 768px) {
    flex: 1;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #999;
  background: #2a2a2a;
  border-radius: 8px;

  p {
    margin: 0.5rem 0;
  }

  button {
    margin-top: 1.5rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #fff;
  padding: 2rem;
`;

export default DomainsList;
